// eslint-disable-next-line no-restricted-imports
import {
  Description,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  CloseButton as HeadlessCloseButton,
  Dialog as HeadlessDialog,
} from "@headlessui/react";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { CloseButton } from "./CloseButton";

import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  /** The title is used for accessibility purposes, and is displayed at the top of the dialog. */
  title?: ReactNode;
  /** The description is used for accessibility purposes. */
  description?: ReactNode;
  /** Use the `children` prop to render what you want in the body of the dialog. */
  children: ReactNode;
  className?: string;
  titleClassName?: string;
  dialogPanelClassName?: string;
  /** When false, hides the close button and prevents closing via Escape or backdrop click. Defaults to true. */
  dismissible?: boolean;
};

export const Dialog: FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  className,
  titleClassName,
  dialogPanelClassName = "w-[400px]",
  dismissible = true,
}) => {
  const closeAndTrack = () => {
    trackEvent("Close modal", BUTTON_EVENT_CATEGORY);
    onClose();
  };

  return (
    <HeadlessDialog
      className={twMerge("relative z-50", className)}
      open={open}
      onClose={dismissible ? closeAndTrack : () => {}}
    >
      <div className="fixed inset-0 z-50">
        <DialogBackdrop className="absolute inset-0 bg-black/50" />

        <div className="absolute inset-0 flex w-full items-center justify-center p-4 md:p-10">
          <div className="max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg border border-black-tint-90 bg-white p-4 md:p-6">
            <DialogPanel className={dialogPanelClassName}>
              <div className="relative flex items-center justify-between">
                <DialogTitle className={twMerge("text-base font-normal", titleClassName)}>
                  {title}
                </DialogTitle>
                {dismissible && (
                  <HeadlessCloseButton
                    as={CloseButton}
                    className="absolute -right-2 -top-2 md:-right-4 md:-top-4"
                    onClick={closeAndTrack}
                  />
                )}
              </div>
              {description && <Description as="div">{description}</Description>}

              {children}
            </DialogPanel>
          </div>
        </div>
      </div>
    </HeadlessDialog>
  );
};
